import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import BankAccountDropdown from "components/dropdowns/reactselect/BankAccountDropdown";
import BrandDropdown from "components/dropdowns/reactselect/BrandDropdown";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { CurrencyAndExchangeRate, CurrencyAndExchangeRatePicker, ICurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { SectionTemplateBrField } from "components/MainSectionTemplate/SectionTemplateBrField";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import ChooseTemplateButton from "components/MessageTemplate/ChooseTemplateButton";
import { getInvoiceTemplateTranslation } from "components/MessageTemplate/hooks";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { BankAccountSmallDetails, BookkeepingSmallDetails, CompanySmallDetails } from "components/SmallDetails";
import SmallDetails from "components/SmallDetails/SmallDetails";
import LifecycleSteps from "components/Steps/LifecycleSteps";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import moment from "moment";
import { Message } from "semantic-ui-react";
import React, { Dispatch, SetStateAction } from "react";
import { formatDaysSince, formatWrittenDayMonthYear, isNullOrEmptyString } from "utilities";
import ViewInvoiceButtonPart from "../ViewInvoiceButtonPart";
import { getCultureFromLcid, getCurrency } from "utilities/enumutils";
import { useModals } from "Modals/ModalsProvider";
import { CostCenterLabel } from "components/Removables/RemovableCostCenter";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import { ProjectCodeInput } from "components/ProjectCodeInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { PaymentTermInvoiceV2 } from "components/Invoice/PaymentTermInvoiceV2";
import DocumentDueDatePicker from "components/Document/DocumentDueDatePicker";
import { EInvoiceInvoiceV2Compliance } from "../EInvoiceInvoiceV2Compliance";

interface InvoiceV2DraftSectionProps {
  invoice: server.dto.InvoiceV2
  onInvoiceChange: Dispatch<SetStateAction<server.dto.InvoiceV2>>
  refetchInvoice: () => Promise<void>
  validate: () => Promise<boolean>
  onBookkeepingOrCurrencyChange: (newBookkeeping: server.dto.BookkeepingSmall) => void
  initialProductsByParent: server.dto.ProductsByParent;
  missingFields: string[];
  validateForPreview: () => Promise<boolean>;
  einvoiceCompliance: server.dto.EInvoiceInvoiceV2Compliance;
}


export const InvoiceV2DraftSection = (props: InvoiceV2DraftSectionProps) => {
  const invoice = props.invoice;

  const hasOnlyOneBrand = !site.currentEnvironment.hasMultipleBrands;
  const displayCulture = serverReference.templateCultures.find((c) => c.lcid === invoice.lcid);
  const hasDocumentNumber = props.invoice.documentNumber != null;

  const modals = useModals();

  const onTemplateSelected = async (template: server.dto.MessageTemplate, language: server.dto.Languages) => {
    const translation = await getInvoiceTemplateTranslation(template.messageTemplateId, language);
    props.onInvoiceChange(prev => ({
      ...prev,
      textDebitNotes: translation.debitNotes ?? "",
      textCreditNotes: translation.creditNotes ?? "",
      textFooter: translation.footer ?? "",
    }));

    await window.CoreApi.InvoiceV2.UpdateDetails({
      ...getUpdateInvoiceV2DetailsInstruction(invoice),
      textDebitNotes: translation.debitNotes ?? "",
      textCreditNotes: translation.creditNotes ?? "",
      textFooter: translation.footer ?? "",
    });
  };

  const onValueChanged = (key: string, value: any) => {
    if (key === "currencyExchangeRate") {
      props.onInvoiceChange({
        ...invoice,
        currency: value.currency,
        exchangeRate: value.exchangeRate,
      });
    } else if (key === "bankAccount") {
      props.onInvoiceChange({ ...invoice, bankAccount: value });
    } else if (key === "brand") {
      props.onInvoiceChange({ ...invoice, brand: value });
    } else if (key === "dueInDays") {
      props.onInvoiceChange({ ...invoice, dueInDays: value });
    } else if (key === "owner") {
      props.onInvoiceChange({ ...invoice, owner: value });
    } else if (key === "company") {
      props.onInvoiceChange({ ...invoice, company: value });
    } else if (key === "costCenter") {
      props.onInvoiceChange({ ...invoice, costCenter: value });
    } else if (key === "projectCode") {
      props.onInvoiceChange({ ...invoice, projectCode: value });
    } else if (key === "purchaseOrderNumber") {
      props.onInvoiceChange({ ...invoice, purchaseOrderNumber: value });
    } else if (key === "lcid") {
      const result = value as { lcid: server.dto.LCID, template: server.dto.MessageTemplate_InvoiceDocumentTranslation };
      props.onInvoiceChange({
        ...invoice,
        lcid: result.lcid,
        textDebitNotes: result.template.debitNotes,
        textCreditNotes: result.template.creditNotes,
        textFooter: result.template.footer,
      });
    } else if (key === "concerning") {
      props.onInvoiceChange({ ...invoice, concerning: value });
    } else if (key === "textFooter") {
      props.onInvoiceChange({ ...invoice, textFooter: value });
    } else if (key === "textDebitNotes") {
      props.onInvoiceChange({ ...invoice, textDebitNotes: value });
    } else if (key === "textCreditNotes") {
      props.onInvoiceChange({ ...invoice, textCreditNotes: value });
    } else if (key === "reverseCharge") {
      props.onInvoiceChange({ ...invoice, reverseCharge: value });
    } else {
      console.error(`Unknown key ${key} in InvoiceV2DraftSection`);
    }
  }

  const onSuccessfulUpdate = async (key: string, value: any) => {
    if (key === "currencyExchangeRate") {
      props.onBookkeepingOrCurrencyChange(props.invoice.bookkeeping);
    } else if (key === "company") {
      modals.displayFinancialRiskModal(value.companyId);
      await props.refetchInvoice();
    }
  }

  const getUpdateInvoiceV2DetailsInstruction = (invoice: server.dto.InvoiceV2): server.dto.UpdateInvoiceV2DetailsInstruction => ({
    invoiceId: props.invoice.invoiceId,
    bankAccountId: invoice.bankAccount.bankAccountId,
    brandId: invoice.brand.brandId,
    addresseeCompanyId: invoice.company.companyId,
    concerning: invoice.concerning,
    costCenterId: props.invoice.costCenter?.costCenterId,
    currency: invoice.currency,
    dueInDays: invoice.dueInDays,
    projectCode: props.invoice.projectCode,
    purchaseOrderNumber: props.invoice.purchaseOrderNumber,
    exchangeRate: invoice.exchangeRate,
    culture: invoice.lcid,
    ownerUserId: invoice.owner.userId,
    textFooter: invoice.textFooter,
    textDebitNotes: invoice.textDebitNotes,
    textCreditNotes: invoice.textCreditNotes,
    reverseCharge: invoice.reverseCharge,
    collectionMethod: props.invoice.collectionMethod
  });

  const onCompanyChange = async () => {
    await props.refetchInvoice();
  }

  const hideInDraftSince = moment.utc().diff(moment.utc(props.invoice.invoiceDate), "days") === 0;

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    sections={[{
      key: "invoiceDetails",
      type: "regular",
      title: RESX.Invoice.xInvoice,
      // supportArticleId: serverReference.supportArticleIds.whatIsInvoiceing,
      fields: [{
        key: "lifecycleSteps",
        label: "",
        hideLabel: true,
        compact: true,
        render: () => <LifecycleSteps
          documentType="invoice"
          pendingLabel={RESX.Financial.Outstanding}
          completedLabel={RESX.Financial.Settled}
          stepOptions={{
            draft: { disabled: false },
            review: { disabled: false, validate: props.validate },
            pending: { disabled: true },
            completed: { disabled: true }
          }}
          overDue={false}
          activeStep="draft"
          documentId={props.invoice.invoiceId}
        />
      }, SectionTemplateBrField("br"), {
        key: "brand",
        label: RESX.Branding.resxBranding,
        hidden: hasOnlyOneBrand,
        render: () => <div>{invoice.brand?.name}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BrandSmall>) => <BrandDropdown
            options={undefined}
            value={value}
            onChange={(brand) => {
              onChange(brand, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: invoice.brand,
          submitChange: async (brand: server.dto.BrandSmall) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            brandId: brand.brandId,
          })
        },
      }, {
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        editDisabled: hasDocumentNumber,
        render: () => <BookkeepingSmallDetails bookkeeping={props.invoice.bookkeeping} />,
      }, {
        key: "currencyExchangeRate",
        label: RESX.Financial.Currency,
        render: () => <CurrencyAndExchangeRate
          bookkeeping={props.invoice.bookkeeping}
          currency={getCurrency(invoice.currency)}
          exchangeRate={invoice.exchangeRate}
        />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<ICurrencyAndExchangeRate>) => <CurrencyAndExchangeRatePicker
            focusOnMount
            validatorKey="currencyAndExchangeRate"
            bookkeeping={props.invoice.bookkeeping}
            currency={value.currency}
            exchangeRate={value.exchangeRate}
            onChange={onChange}
            onEsc={() => cancel()}
            onEnter={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: { currency: invoice.currency, exchangeRate: invoice.exchangeRate },
          submitChange: async (values: ICurrencyAndExchangeRate) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            currency: values.currency,
            exchangeRate: values.exchangeRate
          }),
        },
      }, {
        key: "bankAccount",
        label: RESX.BankAccount.resxBankAccount,
        render: () => <BankAccountSmallDetails bankAccount={invoice.bankAccount} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BankAccountSmall>) => <BankAccountDropdown
            options={undefined}
            filterByBookkeepingId={props.invoice.bookkeeping.bookkeepingId}
            value={value}
            onChange={(ba) => {
              onChange(ba, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: invoice.bankAccount,
          submitChange: async (bankAccount: server.dto.BankAccountSmall) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            bankAccountId: bankAccount.bankAccountId,
          })
        },
      }, {
        key: "company",
        label: RESX.Company.resxCompany,
        render: () => <CompanySmallDetails company={invoice.company} layout="multiLine" onChange={onCompanyChange} />,
        inPageEdit: {
          render: ({ onChange }) => <div className="main-section-template-fields-padding">
            <CompanyModalPicker
              startOpen
              value={null}
              onChange={v => onChange(v, true)}
              modalTitle={RESX.Company.resxCompany}
              addressType="administrative"
              validatorKey="company"
              required={false}
              canCreateNew={false}
            />
          </div>,
          fullWidth: false,
          type: "custom",
          initialValue: invoice.company,
          submitChange: async (c: server.dto.CompanySmall) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            addresseeCompanyId: c.companyId,
          }),
        },
      }, {
        key: "einvoiceCompliance",
        label: "Peppol",
        hidden: props.einvoiceCompliance.sender.value === "countryNotSupported",
        render: () => <EInvoiceInvoiceV2Compliance compliance={props.einvoiceCompliance} />
      }, {
        key: "dueInDays",
        label: RESX.Invoice.PaymentTerm,
        render: () => <PaymentTermInvoiceV2 dueInDays={invoice.dueInDays} sendDate={moment.utc().startOf("day")} />,
        inPageEdit: {
          render: ({ value, onChange, cancel, submit }: RenderEditOptions<number>) => {
            return <div className="flex gap">
              <RequiredNumberInput
                autoFocus
                width={60}
                validatorKey="dueInDays"
                value={value}
                onChange={(v: number) => onChange(v)}
                onEnterPressed={(v: number) => onChange(v, true)}
                onEscPressed={() => cancel()}
                decimalPoints={0}
                labelContent={RESX.DateTime.days}
                minValue={0}
                maxValue={serverReference.constants.invoice.maxDueInDays}
              />

              <DocumentDueDatePicker
                onChange={(_, dueInDays, fromCalendar) => onChange(dueInDays, fromCalendar)}
                minDate={moment.utc().add(1, "day").startOf("day")}
                sendDate={moment.utc().startOf("day")}
                dueInDays={value}
                inPageEdit={{
                  cancel,
                  submit
                }}
              />

              {/* <FormDatePicker
                value={dueDate}
                validatorKey="dueDate"
                minDate={moment.utc().startOf("day").add(1, "day")}
                maxDate={maxDueDate}
                onChange={(date) => {
                  const days = date.diff(moment.utc().startOf("day"), "days");

                  onChange(days);
                }}
              /> */}
            </div>
          },
          type: "confirmOrCancelButton",
          initialValue: invoice.dueInDays,
          submitChange: (dueInDays: number) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            dueInDays,
          })
        },
      }, {
        key: "inDraftSince",
        label: RESX.DocumentLifeCycle.InDraft,
        hidden: hideInDraftSince,
        render: () => <SmallDetails
          label={`${RESX.DateTime.Since} ${formatDaysSince(props.invoice.invoiceDate)}`}
          subLabel={formatWrittenDayMonthYear(props.invoice.invoiceDate)}
        />
      }, {
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        hidden: !site.currentEnvironment.hasCostCenters,
        isEmpty: !invoice.costCenter,
        render: () => <CostCenterLabel value={invoice.costCenter} />,
        inPageEdit: {
          render: ({ onChange }) => <div className="main-section-template-fields-padding">
            <CostCenterModalPicker
              canCreateNew={false}
              startOpen
              value={null}
              onChange={v => onChange(v, true)}
              validatorKey="costCenter"
              required={false}
            />
          </div>,
          fullWidth: false,
          type: "custom",
          initialValue: invoice.costCenter,
          submitChange: async (costCenter: server.dto.CostCenter) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            costCenterId: costCenter.costCenterId,
          })
        }
      },
      {
        key: "projectCode",
        label: RESX.Show.ProjectCode,
        isEmpty: !invoice.projectCode,
        render: () => <div>{invoice.projectCode}</div>,
        hidden: site.currentEnvironment.projectCode === "hidden",
        inPageEdit: {
          render: ({ value, onChange, cancel, submit }: RenderEditOptions<string>) => <ProjectCodeInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            focusOnMount
            canCreateNew={false}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.projectCode,
          submitChange: async (projectCode: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            projectCode,
          })
        }
      }, {
        key: "purchaseOrderNumber",
        label: RESX.Invoice.PurchaseOrderNumber,
        isEmpty: !invoice.purchaseOrderNumber,
        hidden: !site.currentEnvironment.purchaseOrderNumber,
        render: () => <div>{invoice.purchaseOrderNumber}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.document.maxPurchaseOrderNumberLength}
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.purchaseOrderNumber,
          submitChange: (purchaseOrderNumber: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            purchaseOrderNumber,
          }),
        },
      }, {
        key: "owner",
        label: RESX.GeneralLabels.Owner,
        render: () => <UserWithAvatar user={invoice.owner} />,
        inPageEdit: {
          render: ({ onChange }) => <div className="main-section-template-fields-padding">
            <UserModalPicker
              startOpen
              value={null}
              onChange={v => onChange(v, true)}
              validatorKey="owner"
              required={false}
              canCreateNew={false}
            />
          </div>,
          fullWidth: false,
          type: "custom",
          initialValue: invoice.owner,
          submitChange: async (owner: server.dto.UserSmall) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            ownerUserId: owner.userId,
          }),
        },
      },
        // {
        //   key: "eInvoicing",
        //   label: "e-Invoicing (BETA)",
        //   render: () => <div>
        //     <Message color="yellow">Not enabled for bookkeeping</Message>
        //     <Message color="red">Cannot determine if {invoice.company?.name} can receive e-Invoices, because registration info is missing.</Message>
        //     <Message color="red">{invoice.company?.name} cannot receive e-Invoices</Message>
        //     <Message color="green">{invoice.company?.name} can receive invoice electronically</Message>
        //   </div>
        // },
      ]
    }, {
      key: "documentDetails",
      type: "regular",
      title: <div className="flex justify-space-between">
        <div>{RESX.InvoiceDocument.resxInvoiceDocument}</div>
        <div style={{ fontSize: 14 }}>
          <ChooseTemplateButton
            type="invoiceV2DocumentTemplate"
            buttonLabel={RESX.InvoiceDocument.InvoiceTemplate}
            onTemplateSelected={onTemplateSelected}
          />
        </div>
      </div>,

      fields: [{
        key: "lcid",
        label: RESX.Language.resxLanguage,
        render: () => <CultureLabel culture={displayCulture} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<{ lcid: server.dto.LCID, template: server.dto.MessageTemplate_InvoiceDocumentTranslation }>) => <CulturesTemplateDropdown
            value={value.lcid}
            onChange={async (lcid) => {
              const preferredTemplateRes = await window.CoreApi.MessageTemplate.PreferredInvoiceDocumentTemplateTranslation({ language: getCultureFromLcid(lcid).language, type: "invoiceV2DocumentTemplate" });
              const preferredTemplate = preferredTemplateRes.data?.data;
              const template: server.dto.MessageTemplate_InvoiceDocumentTranslation = {
                language: preferredTemplate?.language ?? getCultureFromLcid(lcid).language,
                debitNotes: preferredTemplate?.debitNotes ?? "",
                creditNotes: preferredTemplate?.creditNotes ?? "",
                footer: preferredTemplate?.footer ?? "",
              }
              onChange({ lcid, template: template }, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: invoice.lcid,
          submitChange: async (value: { lcid: server.dto.LCID, template: server.dto.MessageTemplate_InvoiceDocumentTranslation }) => {
            return window.CoreApi.InvoiceV2.UpdateDetails({
              ...getUpdateInvoiceV2DetailsInstruction(invoice),
              culture: value.lcid,
              textDebitNotes: value.template.debitNotes,
              textCreditNotes: value.template.creditNotes,
              textFooter: value.template.footer,
            });
          }
        },
      }, {
        key: "concerning",
        label: RESX.InvoiceDocument.Concerning,
        isEmpty: !invoice.concerning,
        render: () => <OptionalTextAreaDetails>{invoice.concerning}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.document.maxConcerningLength}
            value={value}
            onChange={onChange}
            onCtrlEnter={submit}
            onEsc={cancel}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.concerning,
          submitChange: (concerning: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            concerning,
          }),
        },
      },
      {
        key: "debitInvoiceHeader",
        hidden: invoice.isCredit,
        render: () => <h3>{RESX.Invoice.DebitInvoice}</h3>,
        hideLabel: true,
        label: "",
      },
      {
        key: "creditInvoiceHeader",
        hidden: !invoice.isCredit,
        render: () => <h3>{RESX.Invoice.CreditInvoice}</h3>,
        hideLabel: true,
        label: "",
      }, {
        key: "textDebitNotes",
        label: RESX.Note.Notes,
        isEmpty: isNullOrEmptyString(invoice.textDebitNotes),
        hidden: invoice.isCredit,
        render: () => <OptionalTextAreaDetails>{invoice.textDebitNotes}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.messageTemplate.maxTranslationDebitCreditNotesLength}
            value={value}
            onChange={(v) => { onChange(v); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.textDebitNotes,
          submitChange: (textDebitNotes: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            textDebitNotes: textDebitNotes,
          }),
        },
      }, {
        key: "textCreditNotes",
        label: RESX.Note.Notes,
        isEmpty: isNullOrEmptyString(invoice.textCreditNotes),
        hidden: !invoice.isCredit,
        render: () => <OptionalTextAreaDetails>{invoice.textCreditNotes}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.messageTemplate.maxTranslationDebitCreditNotesLength}
            value={value}
            onChange={(v) => { onChange(v); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.textCreditNotes,
          submitChange: (textCreditNotes: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            textCreditNotes: textCreditNotes,
          }),
        },
      },
      {
        key: "reverseChargeInformationHeader",
        render: () => <h3>{RESX.ReverseCharge.xReverseCharge}</h3>,
        hideLabel: true,
        label: "",
      },
      {
        key: "reverseCharge",
        label: RESX.EmbedWizard.Display,
        render: () => <YesNoText value={invoice.reverseCharge} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: invoice.reverseCharge,
          submitChange: (reverseCharge: boolean) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            reverseCharge,
          }),
        }
      }, {
        key: "otherInfoHeader",
        render: () => <h3>{RESX.Company.OtherDetails}</h3>,
        hideLabel: true,
        label: "",
      }, {
        key: "textFooter",
        label: "Footer",
        isEmpty: !invoice.textFooter,
        render: () => <OptionalTextAreaDetails>{invoice.textFooter}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.messageTemplate.maxTranslationFooterLength}
            value={value}
            onChange={onChange}
            onCtrlEnter={submit}
            onEsc={cancel}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: invoice.textFooter,
          submitChange: (textFooter: string) => window.CoreApi.InvoiceV2.UpdateDetails({
            ...getUpdateInvoiceV2DetailsInstruction(invoice),
            textFooter: textFooter,
          }),
        },
      }, {
        key: "previewDocument",
        label: "",
        hideLabel: true,
        render: () => <div className="flex-column gap">
          {props.missingFields.length > 0 && <Message color="red">
            <div className="flex">
              <div className="grow1">
                <div className="margin-bottom-small">
                  <strong>Cannot preview document. Provide:</strong>
                </div>
                <ul>
                  {props.missingFields.map(f => <li key={f}>{f}</li>)}
                </ul>
              </div>
            </div>
          </Message>}

          {props.missingFields.length == 0 && <ViewInvoiceButtonPart
            documentNumber={props.invoice.documentNumber}
            label={RESX.Invoice.ViewInvoice}
            documentId={props.invoice.invoiceId}
            onValidate={props.validateForPreview}
          />}
        </div>
      }]
    }]}
  />
}
